import {
  Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Text, Flex, Badge,
  Divider, useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../Hooks/useAuth";
import { api } from "../../../services/api";
import swal from "sweetalert";
import { FiTruck, FiMapPin, FiUser, FiCheckCircle, FiXCircle, FiAlertCircle, FiPackage, FiHome } from "react-icons/fi";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

const STATUS_CONFIG = {
  pending:          { label: "Aguardando disponibilidade", color: "orange", bg: "rgba(230,126,34,.1)",  icon: FiAlertCircle },
  out_for_delivery: { label: "Saiu para entrega",          color: "#2980b9", bg: "rgba(41,128,185,.1)", icon: FiTruck },
  delivered:        { label: "Entregue",                   color: "#27ae60", bg: "rgba(39,174,96,.1)",  icon: FiCheckCircle },
  collected:        { label: "Retirado",                   color: "#27ae60", bg: "rgba(39,174,96,.1)",  icon: FiCheckCircle },
  cancelled:        { label: "Cancelado",                  color: "#e74c3c", bg: "rgba(231,76,60,.1)",  icon: FiXCircle },
};

const CONFIRM_MESSAGES = {
  out_for_delivery: "Confirmar que o pedido saiu para entrega?",
  delivered:        "Confirmar que o pedido foi entregue ao cliente?",
  collected:        "Confirmar que o cliente retirou o pedido?",
  cancelled:        "Tem certeza que deseja cancelar este pedido? Esta ação não pode ser desfeita.",
};

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <Flex justify="space-between" align="flex-start" py="6px" borderBottom="1px solid rgba(0,74,51,.06)">
      <Text fontSize="12px" color="gray.400" fontWeight="600" flexShrink={0} mr="12px">{label}</Text>
      <Text fontSize="12px" color="gray.700" textAlign="right">{value}</Text>
    </Flex>
  );
}

function Section({ title, Icon, children }) {
  return (
    <Box mb="16px">
      <Flex align="center" gap="7px" mb="10px">
        <Icon size={13} color={GREEN} />
        <Text fontSize="11px" fontWeight="800" color={GREEN} textTransform="uppercase" letterSpacing="0.08em">
          {title}
        </Text>
      </Flex>
      <Box bg="rgba(0,74,51,.025)" borderRadius="10px" p="4px 12px">
        {children}
      </Box>
    </Box>
  );
}

function formatPhone(phone) {
  if (!phone) return null;
  const c = phone.replace(/\D/g, "");
  return c.length === 11
    ? c.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3")
    : phone;
}

function formatCPF(doc) {
  if (!doc) return null;
  return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

export function Logistics({ orderId, updateOrderStatus, onPaymentMarkedPaid, deliveryStatus, isPaid }) {
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [order, setOrder]                       = useState({});
  const [batch, setBatch]                       = useState({});
  const [product, setProduct]                   = useState({});
  const [collectionAddress, setCollectionAddress]     = useState(null);
  const [collectionAddressCity, setCollectionAddressCity]   = useState(null);
  const [collectionAddressState, setCollectionAddressState] = useState(null);
  const [client, setClient]   = useState({});
  const [payment, setPayment] = useState({});
  const [loading, setLoading] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const fetchedRef = useRef(false);

  async function fetchOrder() {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    try {
      const response = await api.get(`/api/v1/batch_orders/${orderId}`);
      setOrder(response.data.batch_order);
      setBatch(response.data.batch);
      setProduct(response.data.product);
      setCollectionAddress(response.data.collection_address || null);
      setCollectionAddressCity(response.data.collection_address_city || null);
      setCollectionAddressState(response.data.collection_address_state || null);
      setClient(response.data.user);
      setPayment(response.data.payment);
    } catch (err) {
      console.error("Error fetching order:", err);
    } finally {
      setLoading(false);
    }
  }

  function handleOpen() {
    onOpen();
    fetchOrder();
  }

  async function handleDeliveryStatus(e, status) {
    e.preventDefault();
    const msg = CONFIRM_MESSAGES[status] || "Confirmar esta ação?";
    const confirmed = window.confirm(msg);
    if (!confirmed) return;

    const formData = new FormData();
    formData.append("batch_order[delivery_status]", status);
    if (status === "delivered" || status === "collected") {
      formData.append("batch_order[recieved_at]", new Date().toISOString());
    }
    if (status === "cancelled") {
      formData.append("batch_order[cancelled_at]", new Date().toISOString());
      formData.append("batch_order[status]", status);
    }

    try {
      const response = await api.put(`/api/v1/batch_orders/${orderId}`, formData, {
        headers: {
          "access-token": user.token, client: user.client, uid: user.uid,
          accept: "application/json", contentType: "multipart/form-data",
        },
      });
      if (response.status === 200) {
        setOrder(prev => ({ ...prev, delivery_status: response.data.delivery_status }));
        updateOrderStatus(response.data.id, response.data.delivery_status);
        swal({ title: "Atualizado!", text: "Status atualizado com sucesso.", icon: "success" });
        onClose();
      } else {
        swal({ title: "Erro", text: "Algo deu errado ao atualizar o status.", icon: "error" });
      }
    } catch (err) {
      console.error("Error updating delivery status:", err);
      swal({ title: "Erro", text: "Falha na comunicação com o servidor.", icon: "error" });
    }
  }

  // Manual admin override for when the automatic Iugu confirmation misses a
  // real payment. Uses all server-side defaults (valor esperado, agora) — no
  // custom-amount form, since that's the exceptional case, not the norm.
  // Marking one order's payment as paid affects every order under that same
  // payment, so the response's full `batch_orders` list is passed up to the
  // parent table to keep every row in sync, not just this one.
  async function handleMarkPaymentAsPaid() {
    const confirmed = window.confirm("Confirmar que o pagamento deste pedido foi recebido?");
    if (!confirmed) return;

    setMarkingPaid(true);
    try {
      const response = await api.post(`/api/v1/batch_orders/${orderId}/mark_as_paid`);
      setOrder(prev => ({ ...prev, paid: true, status: "paid" }));
      onPaymentMarkedPaid?.(response.data.batch_orders);
      swal({ title: "Pagamento confirmado!", text: "O pedido e os demais pedidos deste pagamento foram marcados como pagos.", icon: "success" });
    } catch (err) {
      const message = err.response?.data?.error || "Falha na comunicação com o servidor.";
      swal({ title: "Erro", text: message, icon: "error" });
    } finally {
      setMarkingPaid(false);
    }
  }

  const currentStatus = STATUS_CONFIG[order.delivery_status || deliveryStatus];
  const StatusIcon = currentStatus?.icon || FiAlertCircle;
  const isDelivery = order.delivery_method === "delivery" || order.delivery_method === "delivery-express";
  const isCollect  = order.delivery_method === "collect";
  const isFinal    = ["delivered", "collected", "cancelled"].includes(order.delivery_status);
  const canAct     = order.paid && !isFinal;

  return (
    <>
      <Button
        size="xs" borderRadius="8px" px="10px" h="26px"
        bg={GREEN} color={ORANGE} fontWeight="700" fontSize="11px"
        _hover={{ opacity: 0.85 }} onClick={handleOpen}
      >
        Detalhes
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
        <ModalOverlay bg="rgba(0,0,0,.45)" backdropFilter="blur(2px)" />
        <ModalContent borderRadius="20px" overflow="hidden" mx="16px">

          {/* Header */}
          <ModalHeader
            bg={GREEN} py="16px" px="24px"
            display="flex" alignItems="center" justifyContent="space-between"
          >
            <Box>
              <Text fontSize="11px" color="rgba(255,255,255,.45)" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
                Pedido
              </Text>
              <Text fontSize="20px" fontWeight="900" color={ORANGE} lineHeight="1.2">
                #{orderId}
              </Text>
            </Box>
            {/* Status badge */}
            <Flex
              align="center" gap="7px" px="12px" py="7px" borderRadius="99px"
              bg={currentStatus?.bg || "rgba(255,255,255,.1)"}
            >
              <StatusIcon size={13} color={currentStatus?.color || "white"} />
              <Text fontSize="12px" fontWeight="800" color={currentStatus?.color || "white"}>
                {currentStatus?.label || "—"}
              </Text>
            </Flex>
            <ModalCloseButton color="rgba(255,255,255,.6)" _hover={{ color: "white", bg: "transparent" }} top="14px" right="16px" />
          </ModalHeader>

          <ModalBody px="24px" py="20px" bg="#fafbfc">
            {loading ? (
              <Flex justify="center" py="40px">
                <Box style={{ width: 28, height: 28, border: `3px solid ${GREEN}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </Flex>
            ) : (<>

              {/* Payment guard */}
              {!order.paid && (
                <Flex
                  align="center" gap="10px" bg="rgba(252,143,4,.08)" border="1px solid rgba(252,143,4,.3)"
                  borderRadius="10px" p="12px 16px" mb="16px"
                >
                  <FiAlertCircle size={16} color={ORANGE} />
                  <Text fontSize="12px" fontWeight="700" color="#b76800">
                    Pagamento pendente — ações de entrega serão habilitadas após confirmação do pagamento.
                  </Text>
                </Flex>
              )}

              {/* Produto */}
              <Section title="Produto" Icon={FiPackage}>
                <InfoRow
                  label="Item"
                  value={`${order.quantity || "—"} ${batch.unity_of_measurement || ""}${(order.quantity || 0) > 1 ? "s" : ""} de ${product.name || "—"}`}
                />
                <InfoRow
                  label="Unidades"
                  value={`${(order.quantity || 0) * (product.quantity_per_box || 0)} unidades`}
                />
              </Section>

              {/* Cliente */}
              <Section title="Cliente" Icon={FiUser}>
                <InfoRow label="Nome"    value={client.full_name || client.email || "—"} />
                <InfoRow label="CPF"     value={formatCPF(client.document_number)} />
                <InfoRow label="Celular" value={formatPhone(client.phone_number)} />
                <InfoRow label="E-mail"  value={client.email} />
              </Section>

              {/* Endereço cadastrado no perfil do cliente */}
              <Section title="Endereço Cadastrado do Cliente" Icon={FiHome}>
                {client.address_street ? (
                  <>
                    <InfoRow
                      label="Rua"
                      value={`${client.address_street}, nº ${client.address_number || "—"}${client.address_complement ? `, ${client.address_complement}` : ""}`}
                    />
                    <InfoRow label="Bairro" value={client.address_neighborhood || null} />
                    <InfoRow label="CEP" value={client.address_postal_code || null} />
                  </>
                ) : (
                  <Text fontSize="12px" color="gray.500" py="6px">
                    Cliente não possui endereço cadastrado no perfil.
                  </Text>
                )}
              </Section>

              {/* Endereço */}
              {(isDelivery || isCollect) && (
                <Section title={isCollect ? "Local de Retirada" : "Endereço de Entrega"} Icon={FiMapPin}>
                  {isDelivery && (
                    <InfoRow label="Endereço" value={order.delivery_address || "—"} />
                  )}
                  {isCollect && collectionAddress?.address_street && (
                    <>
                      <InfoRow
                        label="Rua"
                        value={`${collectionAddress.address_street}, nº ${collectionAddress.address_number}${collectionAddress.address_complement ? `, ${collectionAddress.address_complement}` : ""}`}
                      />
                      <InfoRow
                        label="Bairro"
                        value={collectionAddress.address_neighborhood || null}
                      />
                      <InfoRow
                        label="Cidade / Estado"
                        value={[
                          collectionAddressCity?.name,
                          collectionAddressState?.acronym,
                        ].filter(Boolean).join(" — ") || null}
                      />
                      <InfoRow
                        label="CEP"
                        value={collectionAddress.address_postal_code || null}
                      />
                    </>
                  )}
                  {isCollect && !collectionAddress?.address_street && (
                    <Text fontSize="12px" color="gray.500" py="6px">
                      Endereço de retirada não cadastrado para este lote.
                    </Text>
                  )}
                </Section>
              )}

              {/* Observações */}
              {payment?.observations && (
                <Section title="Observações" Icon={FiAlertCircle}>
                  <Text fontSize="12px" color="gray.700" py="6px" lineHeight="1.6">
                    {payment.observations}
                  </Text>
                </Section>
              )}
            </>)}
          </ModalBody>

          <ModalFooter
            bg="white" borderTop="1px solid rgba(0,74,51,.08)"
            px="24px" py="16px" gap="8px" justifyContent="flex-end" flexWrap="wrap"
          >
            {isFinal ? (
              <Badge
                px="12px" py="6px" borderRadius="99px" fontSize="12px" fontWeight="800"
                colorScheme={order.delivery_status === "cancelled" ? "red" : "green"}
              >
                {currentStatus?.label}
              </Badge>
            ) : canAct ? (<>
              {/* Cancel */}
              {order.delivery_status !== "cancelled" && (
                <Button
                  size="sm" colorScheme="red" variant="outline" borderRadius="8px"
                  fontSize="12px" fontWeight="700"
                  onClick={e => handleDeliveryStatus(e, "cancelled")}
                >
                  Cancelar pedido
                </Button>
              )}
              {/* Delivery: pending → out_for_delivery */}
              {isDelivery && order.delivery_status === "pending" && (
                <Button
                  size="sm" bg={GREEN} color={ORANGE} borderRadius="8px"
                  fontSize="12px" fontWeight="800"
                  _hover={{ opacity: 0.88 }}
                  leftIcon={<FiTruck size={13} />}
                  onClick={e => handleDeliveryStatus(e, "out_for_delivery")}
                >
                  Saiu para entrega
                </Button>
              )}
              {/* Delivery: out_for_delivery → delivered */}
              {isDelivery && order.delivery_status === "out_for_delivery" && (
                <Button
                  size="sm" bg={GREEN} color={ORANGE} borderRadius="8px"
                  fontSize="12px" fontWeight="800"
                  _hover={{ opacity: 0.88 }}
                  leftIcon={<FiCheckCircle size={13} />}
                  onClick={e => handleDeliveryStatus(e, "delivered")}
                >
                  Marcar como entregue
                </Button>
              )}
              {/* Collect: pending → collected */}
              {isCollect && order.delivery_status === "pending" && (
                <Button
                  size="sm" bg={GREEN} color={ORANGE} borderRadius="8px"
                  fontSize="12px" fontWeight="800"
                  _hover={{ opacity: 0.88 }}
                  leftIcon={<FiCheckCircle size={13} />}
                  onClick={e => handleDeliveryStatus(e, "collected")}
                >
                  Marcar como retirado
                </Button>
              )}
            </>) : !order.paid ? (
              <Button
                size="sm" bg={GREEN} color={ORANGE} borderRadius="8px"
                fontSize="12px" fontWeight="800" _hover={{ opacity: 0.88 }}
                leftIcon={<FiCheckCircle size={13} />}
                isLoading={markingPaid}
                onClick={handleMarkPaymentAsPaid}
              >
                Marcar pagamento como pago
              </Button>
            ) : null}

            <Button
              size="sm" variant="ghost" colorScheme="gray" borderRadius="8px"
              fontSize="12px" fontWeight="700" onClick={onClose}
            >
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
